import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE } from "./ui-bits";

/* Illustrative population model: total testosterone (nmol/L) plateaus ~21.5 to the early 30s,
   then declines ~1–2% per year (central estimate 1.2%). Not a diagnostic tool. */
const PEAK = 21.5;
const PEAK_AGE = 30;
const RATE = { low: 0.01, mid: 0.012, high: 0.02 };
const AGE_MIN = 20, AGE_MAX = 85;

export const typicalT = (age, rate = RATE.mid) => (age <= PEAK_AGE ? PEAK : PEAK * (1 - rate) ** (age - PEAK_AGE));
const r1 = (n) => Math.round(n * 10) / 10;

const W = 560, H = 400;
const PAD = { l: 62, r: 18, t: 30, b: 44 };
const T_MIN = 6, T_MAX = 26;
const x = (age) => PAD.l + ((age - AGE_MIN) / (AGE_MAX - AGE_MIN)) * (W - PAD.l - PAD.r);
const y = (t) => H - PAD.b - ((t - T_MIN) / (T_MAX - T_MIN)) * (H - PAD.t - PAD.b);

const AGES = Array.from({ length: AGE_MAX - AGE_MIN + 1 }, (_, i) => AGE_MIN + i);
const path = (rate) => AGES.map((a, i) => `${i ? "L" : "M"}${x(a).toFixed(1)},${y(typicalT(a, rate)).toFixed(1)}`).join(" ");
const midPath = path(RATE.mid);
const bandPath = `${path(RATE.low)} ${AGES.slice().reverse().map((a) => `L${x(a).toFixed(1)},${y(typicalT(a, RATE.high)).toFixed(1)}`).join(" ")} Z`;
const areaPath = `${midPath} L${x(AGE_MAX)},${H - PAD.b} L${x(AGE_MIN)},${H - PAD.b} Z`;
const Y_TICKS = [6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26];
const X_TICKS = [20, 30, 40, 50, 60, 70, 80];

export default function LevelChart() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const drift = useTransform(scrollYProgress, [0, 1], [36, -36]);
  const [age, setAge] = useState(45);

  const mid = typicalT(age), lo = typicalT(age, RATE.high), hi = typicalT(age, RATE.low);
  const dropPct = Math.round((1 - mid / PEAK) * 100);
  const nearLow = mid < 12;

  return (
    <motion.div ref={ref} style={{ y: drift }} className="relative" data-testid="hero-level-chart">
      <div className="card-line rounded-2xl p-5 sm:p-7 relative overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <p className="mono-label text-dusk">TYPICAL TESTOSTERONE BY AGE — ILLUSTRATIVE</p>
          <p className="mono-label text-teal">TOTAL T · NMOL/L</p>
        </div>

        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto select-none" role="img" aria-label={`Chart of typical total testosterone by age. At ${age} the typical level is about ${r1(mid)} nmol/L.`}>
          <defs>
            <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00E5C0" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#00E5C0" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* y-axis scale bar */}
          <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={H - PAD.b} stroke="rgba(244,246,248,0.25)" />
          {Y_TICKS.map((t) => (
            <g key={t}>
              <line x1={PAD.l - 5} y1={y(t)} x2={PAD.l} y2={y(t)} stroke="rgba(244,246,248,0.35)" />
              <line x1={PAD.l} y1={y(t)} x2={W - PAD.r} y2={y(t)} stroke="rgba(244,246,248,0.04)" />
              <text x={PAD.l - 9} y={y(t) + 3.5} fill="#8A94A6" fontSize="10" fontFamily="JetBrains Mono" textAnchor="end">{t}</text>
            </g>
          ))}
          <text transform={`translate(14 ${(PAD.t + H - PAD.b) / 2}) rotate(-90)`} fill="#64748B" fontSize="10" fontFamily="JetBrains Mono" textAnchor="middle" letterSpacing="2">NMOL/L</text>

          {/* reference lines */}
          <line x1={PAD.l} y1={y(26)} x2={W - PAD.r} y2={y(26)} stroke="rgba(0,229,192,0.35)" strokeDasharray="4 6" />
          <text x={W - PAD.r - 4} y={y(26) - 6} fill="#64748B" fontSize="9.5" fontFamily="JetBrains Mono" textAnchor="end">UPPER REF ~30</text>
          <line x1={PAD.l} y1={y(10)} x2={W - PAD.r} y2={y(10)} stroke="rgba(255,77,77,0.45)" strokeDasharray="4 6" />
          <text x={W - PAD.r - 4} y={y(10) + 13} fill="#64748B" fontSize="9.5" fontFamily="JetBrains Mono" textAnchor="end">LOWER REF ~10</text>

          {/* x-axis — clickable ages */}
          {X_TICKS.map((a) => (
            <g key={a} className="cursor-pointer" onClick={() => setAge(a)} data-testid={`chart-age-${a}`}>
              <line x1={x(a)} y1={PAD.t} x2={x(a)} y2={H - PAD.b} stroke="rgba(244,246,248,0.05)" />
              <rect x={x(a) - 16} y={H - PAD.b + 6} width="32" height="20" rx="6" fill={a === age ? "rgba(0,229,192,0.15)" : "transparent"} />
              <text x={x(a)} y={H - PAD.b + 20} fill={a === age ? "#00E5C0" : "#8A94A6"} fontSize="11" fontFamily="JetBrains Mono" textAnchor="middle">{a}</text>
            </g>
          ))}
          <text x={PAD.l} y={H - 6} fill="#64748B" fontSize="9.5" fontFamily="JetBrains Mono" letterSpacing="2">AGE (YEARS)</text>

          {/* 1–2 %/yr band, central curve, area */}
          <motion.path d={bandPath} pointerEvents="none" fill="rgba(0,229,192,0.08)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 1.2 }} />
          <motion.path d={areaPath} pointerEvents="none" fill="url(#areaFill)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.6, delay: 1.4 }} />
          <motion.path d={midPath} pointerEvents="none" fill="none" stroke="#00E5C0" strokeWidth="2.5" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 0.9, ease: EASE }} />

          {/* selected age marker */}
          <g transform={`translate(${x(age)} ${y(mid)})`} style={{ transition: "transform .5s cubic-bezier(.16,1,.3,1)" }} pointerEvents="none">
            <line x1="0" y1="0" x2="0" y2={H - PAD.b - y(mid)} stroke="rgba(0,229,192,0.45)" strokeDasharray="3 4" />
            <line x1="0" y1="0" x2={PAD.l - x(age)} y2="0" stroke="rgba(0,229,192,0.45)" strokeDasharray="3 4" />
            <circle r="13" fill="rgba(0,229,192,0.14)" />
            <circle r="4.5" fill="#00E5C0" />
            <g transform={`translate(${x(age) > W - 150 ? -124 : 14} -30)`}>
              <rect width="110" height="24" rx="6" fill="#0E131F" stroke="rgba(0,229,192,0.4)" />
              <text x="55" y="16" fill="#F4F6F8" fontSize="11" fontFamily="JetBrains Mono" textAnchor="middle" data-testid="chart-marker-label">{`AGE ${age} · ~${r1(mid)}`}</text>
            </g>
          </g>

          {/* clickable age hit-areas (rendered last so they sit above the curves) */}
          {X_TICKS.map((a) => (
            <rect key={`hit-${a}`} x={x(a) - 20} y={PAD.t} width="40" height={H - PAD.t} fill="transparent" className="cursor-pointer" onClick={() => setAge(a)} data-testid={`chart-age-hit-${a}`}>
              <title>{`Select age ${a}`}</title>
            </rect>
          ))}
        </svg>

        {/* interactive control */}
        <div className="mt-4 rounded-xl border border-line p-4" data-testid="chart-age-control">
          <div className="flex items-center justify-between gap-4">
            <label htmlFor="chart-age" className="mono-label text-dusk">Select your age</label>
            <span className="font-display text-2xl text-teal tracking-wide" data-testid="chart-age-value">{age}</span>
          </div>
          <input id="chart-age" type="range" min={AGE_MIN} max={AGE_MAX} value={age} onChange={(e) => setAge(Number(e.target.value))} className="w-full mt-2 accent-[#00E5C0]" data-testid="chart-age-slider" />
          <p className="text-sm text-bone/90 leading-relaxed mt-3" data-testid="chart-age-result">
            At <span className="text-teal font-semibold">{age}</span>, a typical total testosterone is around{" "}
            <span className="text-teal font-semibold">{r1(mid)} nmol/L</span>
            {age > PEAK_AGE ? <> — roughly <span className="text-teal font-semibold">{dropPct}% lower</span> than at 30 (range {r1(lo)}–{r1(hi)} at 1–2% a year).</> : <> — levels typically plateau through the twenties and early thirties.</>}
            {nearLow && <span className="text-amber-300"> Averages this close to the lower reference limit are exactly why symptoms, not age alone, decide whether to test.</span>}
          </p>
        </div>

        <p className="text-dusk text-xs mt-3 leading-relaxed" data-testid="chart-explainer">
          <span className="text-bone/80">Why the curve falls:</span> after the early thirties, total testosterone declines by about
          1–2% a year on average, so a man of 70 typically has 30–45% less than he did at 30. The shaded band shows that 1–2%
          range. These are population averages — your own level depends on weight, sleep, health and genetics, and only a
          morning blood test can tell you where you sit.{" "}
          <Link to="/learn/normal-testosterone-levels" className="text-teal hover:underline" data-testid="chart-learn-link">What's normal for my age?</Link>
        </p>
      </div>
    </motion.div>
  );
}
